import axios from "../../api/axiosInstance";
import { STATUS, extractErrorInfo } from "./sharedfunction";

/**
 * Fetch list of all apps
 * GET /app/fetch-app-list
 * @param {Object} params - Optional parameters
 * @param {string} params.projectId - Optional project ID to check connection status
 * @returns {Promise<Object>} Response with status, data (apps array), and message
 */
export async function fetchAppList({ projectId } = {}) {
  try {
    const params = {};
    if (projectId) {
      params.projectId = projectId;
    }

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/app/fetch-app-list`,
      {
        params,
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || [],
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error fetching app list:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Create a new app
 * POST /app/createapp
 * @param {Object} params - App creation parameters
 * @param {string} params.name - App name (1-10 characters)
 * @param {string} params.slug_name - Slug name (1-10 characters)
 * @param {string} [params.image_url] - Image URL (max 500 characters)
 * @param {string} [params.description] - Description (max 100 characters)
 * @returns {Promise<Object>} Response with status, data (app object), and message
 */
export async function createApp({ name, slug_name, image_url, description } = {}) {
  try {
    if (!name || name.trim().length === 0) {
      return {
        status: STATUS.ERROR,
        error: "App name is required",
      };
    }

    if (!slug_name || slug_name.trim().length === 0) {
      return {
        status: STATUS.ERROR,
        error: "Slug name is required",
      };
    }

    const payload = {
      name: name.trim(),
      slug_name: slug_name.trim(),
      image_url: image_url?.trim() || null,
      description: description?.trim() || null,
    };

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/app/createapp`,
      payload,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error creating app:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Update an existing app
 * PUT /app/updateapp/:id
 * @param {Object} params - App update parameters
 * @param {string} params.id - App ID (UUID format)
 * @param {string} [params.name] - App name (1-10 characters)
 * @param {string} [params.slug_name] - Slug name (1-10 characters)
 * @param {string} [params.image_url] - Image URL (max 500 characters)
 * @param {string} [params.description] - Description (max 100 characters)
 * @returns {Promise<Object>} Response with status, data (app object), and message
 */
export async function updateApp({ id, name, slug_name, image_url, description } = {}) {
  try {
    if (!id || id.trim().length === 0) {
      return {
        status: STATUS.ERROR,
        error: "App ID is required",
      };
    }

    const payload = {};
    if (name !== undefined) payload.name = name?.trim() || null;
    if (slug_name !== undefined) payload.slug_name = slug_name?.trim() || null;
    if (image_url !== undefined) payload.image_url = image_url?.trim() || null;
    if (description !== undefined) payload.description = description?.trim() || null;

    const response = await axios.put(
      `${process.env.REACT_APP_API_URL}/app/updateapp/${id}`,
      payload,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error updating app:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Delete one or more apps
 * DELETE /app/deleteapp
 * @param {Object} params - Delete parameters
 * @param {Array<string>} params.appIds - Array of app IDs to delete
 * @returns {Promise<Object>} Response with status and data
 */
export async function deleteApps({ appIds } = {}) {
  try {
    if (!appIds || !Array.isArray(appIds) || appIds.length === 0) {
      return {
        status: STATUS.ERROR,
        error: "App IDs array is required",
      };
    }

    const response = await axios.delete(
      `${process.env.REACT_APP_API_URL}/app/deleteapp`,
      {
        data: { appIds },
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error deleting apps:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Get authorize URL for app connection
 * GET /:projectId/app-connection/authorize-url?app_id=xxx
 * @param {Object} params - Authorization parameters
 * @param {string} params.projectId - Project ID
 * @param {string} params.appId - App ID
 * @returns {Promise<Object>} Response with status, data (authorizeUrl), and message
 */
export async function getAuthorizeUrl({ projectId, appId } = {}) {
  try {
    if (!projectId || projectId.trim().length === 0) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!appId || appId.trim().length === 0) {
      return {
        status: STATUS.ERROR,
        error: "App ID is required",
      };
    }

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/${projectId}/app-connection/authorize-url`,
      {
        params: { app_id: appId },
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error getting authorize URL:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Revoke app connection
 * POST /:projectId/app-connection/revoke-connection
 * @param {Object} params - Revoke parameters
 * @param {string} params.projectId - Project ID
 * @param {string} params.appId - App ID
 * @returns {Promise<Object>} Response with status and message
 */
export async function revokeAppConnection({ projectId, appId } = {}) {
  try {
    if (!projectId || projectId.trim().length === 0) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!appId || appId.trim().length === 0) {
      return {
        status: STATUS.ERROR,
        error: "App ID is required",
      };
    }

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/${projectId}/app-connection/revoke-connection`,
      { app_id: appId },
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error revoking app connection:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Fetch the aggregated Slack notification config for a project (Active, channel, events)
 * GET /:projectId/app-connection/slack-notification-config
 * @param {string} projectId
 * @returns {Promise<Object>} Response with status, data ({ connected, active, channelId, channelName, events }), and message
 */
export async function fetchSlackNotificationConfig(projectId) {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/${projectId}/app-connection/slack-notification-config`,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error fetching Slack notification config:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Save the Slack notification config for a project (Active, channel, events)
 * PUT /:projectId/app-connection/slack-notification-config
 * @param {string} projectId
 * @param {Object} params
 * @param {boolean} params.active
 * @param {string} params.channelId
 * @param {Array<string>} params.events
 * @returns {Promise<Object>} Response with status, data ({ connected, active, channelId, channelName, events }), and message
 */
export async function updateSlackNotificationConfig(projectId, { active, channelId, events } = {}) {
  try {
    const response = await axios.put(
      `${process.env.REACT_APP_API_URL}/${projectId}/app-connection/slack-notification-config`,
      {
        active,
        channel_id: channelId,
        events,
      },
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error updating Slack notification config:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Send a direct Slack test message to confirm the channel is reachable
 * POST /:projectId/app-connection/slack-notification-config/test
 * @param {string} projectId
 * @param {string} channelId
 * @returns {Promise<Object>} Response with status, data ({ ok }), and message
 */
export async function testSlackNotificationConfig(projectId, channelId) {
  try {
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/${projectId}/app-connection/slack-notification-config/test`,
      {
        channel_id: channelId,
      },
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error testing Slack notification config:", error);
    return extractErrorInfo(error);
  }
}

/**
 * List configured Slack notification channels for a project's connection
 * GET /:projectId/app-connection/channels?app_id=xxx
 * @param {Object} params
 * @param {string} params.projectId
 * @param {string} params.appId
 * @returns {Promise<Object>} Response with status, data (channels array), and message
 */
export async function listAppConnectionChannels({ projectId, appId } = {}) {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/${projectId}/app-connection/channels`,
      {
        params: { app_id: appId },
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || [],
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error listing app connection channels:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Add a Slack notification channel to a project's connection
 * POST /:projectId/app-connection/channels
 * @param {Object} params
 * @param {string} params.projectId
 * @param {string} params.appId
 * @param {string} params.channelId - Slack channel id, validated server-side
 * @param {Array<string>} [params.notificationTypes]
 * @param {boolean} [params.isDefault]
 * @returns {Promise<Object>} Response with status, data (created channel), and message
 */
export async function addAppConnectionChannel({ projectId, appId, channelId, notificationTypes, isDefault } = {}) {
  try {
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/${projectId}/app-connection/channels`,
      {
        app_id: appId,
        channel_id: channelId,
        notification_types: notificationTypes,
        is_default: isDefault,
      },
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error adding app connection channel:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Update a Slack notification channel's routing tags
 * PATCH /:projectId/app-connection/channels/:id
 * @param {Object} params
 * @param {string} params.projectId
 * @param {string} params.appId
 * @param {string} params.id - AppConnectionChannel row id
 * @param {Array<string>} [params.notificationTypes]
 * @param {boolean} [params.isDefault]
 * @returns {Promise<Object>} Response with status, data (updated channel), and message
 */
export async function updateAppConnectionChannel({ projectId, appId, id, notificationTypes, isDefault } = {}) {
  try {
    const payload = { app_id: appId };
    if (notificationTypes !== undefined) payload.notification_types = notificationTypes;
    if (isDefault !== undefined) payload.is_default = isDefault;

    const response = await axios.patch(
      `${process.env.REACT_APP_API_URL}/${projectId}/app-connection/channels/${id}`,
      payload,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error updating app connection channel:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Remove a Slack notification channel from a project's connection
 * DELETE /:projectId/app-connection/channels/:id?app_id=xxx
 * @param {Object} params
 * @param {string} params.projectId
 * @param {string} params.appId
 * @param {string} params.id - AppConnectionChannel row id
 * @returns {Promise<Object>} Response with status and message
 */
export async function deleteAppConnectionChannel({ projectId, appId, id } = {}) {
  try {
    const response = await axios.delete(
      `${process.env.REACT_APP_API_URL}/${projectId}/app-connection/channels/${id}`,
      {
        params: { app_id: appId },
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error deleting app connection channel:", error);
    return extractErrorInfo(error);
  }
}
